import Phaser from 'phaser'
import { makeButton, makeImageTextButton } from '@/ui/button'
import { AudioManager } from '@/services/AudioManager'
import { SHOP_ITEMS, getPoints, getItemCount, buyItem, type ShopItemDef } from '@/services/shop'

/** 진열대(선반)에 아이템 아이콘을 배치하고, 클릭하면 설명+가격+구매 팝업을 띄운다. */
export class Shop extends Phaser.Scene {
  private dialog?: Phaser.GameObjects.Container
  private balance!: Phaser.GameObjects.Text

  constructor() { super('Shop') }

  create() {
    this.dialog = undefined
    const { width, height } = this.scale
    const hasBg = this.textures.exists('bg_shop')
    this.add.image(width / 2, height / 2, hasBg ? 'bg_shop' : 'bg_lobby').setDisplaySize(width, height)
    if (!hasBg) this.add.rectangle(width / 2, height / 2, width, height, 0x2a1d12, 0.45)
    AudioManager.init(this)
    AudioManager.startBgm()

    // 타이틀(진열대 이미지에 SHOP 간판이 있으면 텍스트는 생략 가능하지만 상점 아이콘만 표시)
    if (!hasBg) {
      if (this.textures.exists('icon_shop')) this.add.image(width * 0.34, height * 0.075, 'icon_shop').setDisplaySize(width * 0.12, width * 0.12)
      this.add.text(width / 2 + width * 0.04, height * 0.07, '상점', {
        fontFamily: 'Arial', fontSize: `${Math.round(width * 0.08)}px`, color: '#ffffff', fontStyle: 'bold',
        stroke: '#3a2a6a', strokeThickness: Math.round(width * 0.012),
      }).setOrigin(0.5)
    }

    // 보유 포인트(좌상단 작은 칩)
    const chip = this.add.graphics().setDepth(5)
    chip.fillStyle(0x140d2e, 0.6); chip.fillRoundedRect(width * 0.03, height * 0.025, width * 0.3, height * 0.04, height * 0.02)
    if (this.textures.exists('icon_point')) this.add.image(width * 0.08, height * 0.045, 'icon_point').setDisplaySize(width * 0.05, width * 0.05).setDepth(6)
    this.balance = this.add.text(width * 0.12, height * 0.045, '', {
      fontFamily: 'Arial', fontSize: `${Math.round(width * 0.038)}px`, color: '#ffe066', fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 3,
    }).setOrigin(0, 0.5).setDepth(6)
    this.refreshBalance()

    // 진열대 선반(3단)에 아이템을 분배(앞 선반부터 채움). 한 선반에 3개 이상이면 아이콘을 약간 작게.
    // 선반 y는 bg 이미지의 가격표 띠 분석값(0.338/0.522/0.706)에 살짝 얹어 배치.
    const shelfY = [0.3, 0.485, 0.67]
    const rowCounts: number[] = []
    let rem = SHOP_ITEMS.length
    for (let r = 0; r < 3; r++) { const c = Math.ceil(rem / (3 - r)); rowCounts.push(c); rem -= c }
    const maxPer = Math.max(...rowCounts)
    const iconSize = width * (maxPer >= 3 ? 0.15 : 0.18)
    const zoneW = width * (maxPer >= 3 ? 0.24 : 0.3)
    let idx = 0
    rowCounts.forEach((c, r) => {
      const y = height * shelfY[r]
      for (let i = 0; i < c; i++) {
        const it = SHOP_ITEMS[idx++]
        const x = width * ((i + 1) / (c + 1))
        const ikey = `item_${it.key}`
        if (this.textures.exists(ikey)) this.add.image(x, y, ikey).setDisplaySize(iconSize, iconSize)
        else this.add.text(x, y, it.emoji, { fontSize: `${Math.round(width * 0.09)}px` }).setOrigin(0.5)
        this.add.zone(x, y, zoneW, width * 0.26).setInteractive({ useHandCursor: true })
          .on('pointerup', () => this.openBuyDialog(it))
      }
    })

    makeImageTextButton(this, width / 2, height * 0.94, 'btn_back', '뒤로', () => this.scene.start('Lobby'),
      { width: width * 0.5, fontSize: width * 0.046 })
  }

  private refreshBalance() { this.balance.setText(`${getPoints().toLocaleString()} P`) }

  /** 아이템 설명 + 가격 + 구매 팝업 */
  private openBuyDialog(it: ShopItemDef) {
    if (this.dialog) return
    const { width, height } = this.scale
    const c = this.add.container(0, 0).setDepth(100)
    const dim = this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.6).setInteractive()
    dim.on('pointerup', () => this.closeDialog())
    const pw = width * 0.82, ph = height * 0.42
    const g = this.add.graphics()
    g.fillStyle(0x1a1140, 0.98); g.fillRoundedRect(width / 2 - pw / 2, height / 2 - ph / 2, pw, ph, ph * 0.1)
    g.lineStyle(2, 0xffffff, 0.3); g.strokeRoundedRect(width / 2 - pw / 2, height / 2 - ph / 2, pw, ph, ph * 0.1)
    const ikey = `item_${it.key}`
    const icon: Phaser.GameObjects.GameObject = this.textures.exists(ikey)
      ? this.add.image(width / 2, height / 2 - ph * 0.28, ikey).setDisplaySize(width * 0.18, width * 0.18)
      : this.add.text(width / 2, height / 2 - ph * 0.28, it.emoji, { fontSize: `${Math.round(width * 0.11)}px` }).setOrigin(0.5)
    const name = this.add.text(width / 2, height / 2 - ph * 0.04, it.name, {
      fontFamily: 'Arial', fontSize: `${Math.round(width * 0.052)}px`, color: '#ffffff', fontStyle: 'bold',
    }).setOrigin(0.5)
    const desc = this.add.text(width / 2, height / 2 + ph * 0.1, it.desc, {
      fontFamily: 'Arial', fontSize: `${Math.round(width * 0.036)}px`, color: '#cdbfff', align: 'center',
      wordWrap: { width: pw * 0.86 },
    }).setOrigin(0.5)
    const price = this.add.text(width / 2, height / 2 + ph * 0.24, `💰 ${it.price.toLocaleString()} P   ·   보유 ${getItemCount(it.key)}`, {
      fontFamily: 'Arial', fontSize: `${Math.round(width * 0.04)}px`, color: '#ffe066', fontStyle: 'bold',
    }).setOrigin(0.5)
    c.add([dim, g, icon, name, desc, price])

    makeButton(this, width / 2 - pw * 0.22, height / 2 + ph * 0.4, '구매', () => {
      if (buyItem(it.key, it.price)) {
        AudioManager.play('ui_click', 0.5)
        this.refreshBalance()
        price.setText(`💰 ${it.price.toLocaleString()} P   ·   보유 ${getItemCount(it.key)}`)
      } else {
        AudioManager.play('invalid', 0.5)
        price.setColor('#ff6b6b'); price.setText('포인트가 부족해요')
        this.time.delayedCall(900, () => price.setColor('#ffe066')
          .setText(`💰 ${it.price.toLocaleString()} P   ·   보유 ${getItemCount(it.key)}`))
      }
    }, { parent: c, width: pw * 0.4, height: height * 0.07, fontSize: width * 0.044, color: 0x5ec8a0, pressColor: 0x49ad88 })
    makeButton(this, width / 2 + pw * 0.22, height / 2 + ph * 0.4, '취소', () => this.closeDialog(),
      { parent: c, width: pw * 0.4, height: height * 0.07, fontSize: width * 0.044, color: 0xa98bff, pressColor: 0x8f6fe8 })
    this.dialog = c
  }

  private closeDialog() {
    this.dialog?.destroy()
    this.dialog = undefined
  }
}
